// The studio layouts declare a font stack of
// 'DM Sans', <studio CJK>, 'Roboto', -apple-system, sans-serif.
// Only Roboto is loaded globally by the app, so on-device the preview silently
// falls back to whatever the OS provides — and Lambda Chromium (which renders the
// download) has none of these installed. Loading the exact same webfonts in BOTH
// the editor and the server-side render template makes the resolved glyphs
// identical everywhere, so the downloaded image matches the preview.
export const STUDIO_FONTS_HREF =
  'https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=Noto+Sans+TC:wght@400;500;600;700&family=Noto+Sans+SC:wght@400;500;600;700&display=swap';

// The app's global font stack (mui-theme, scss) lists 'Noto Sans TC'/'Noto Sans SC'
// but never loads their @font-face (they're commented out), so app CJK text uses
// the system font. If we loaded Google's Noto under those exact names, the whole
// app would suddenly adopt the webfont and look heavier. So we rename the two CJK
// families to studio-only names — the app's Noto reference stays unsatisfied
// (system fallback, unchanged) while the studio layouts opt in explicitly.
export const STUDIO_FONT_TC = 'PPStudio Noto TC';
export const STUDIO_FONT_SC = 'PPStudio Noto SC';

const FAMILY_RENAMES: Array<[string, string]> = [
  ['Noto Sans TC', STUDIO_FONT_TC],
  ['Noto Sans SC', STUDIO_FONT_SC]
];

let studioFontsPromise: Promise<void> | null = null;

// Fetches the studio webfont stylesheet once, renames the CJK families so they
// can't leak onto the rest of the app, and injects the result. Safe to call
// repeatedly and in non-browser contexts. Server render waits on
// document.fonts.ready (+ networkidle2), so the async inject is fine there.
export const ensureStudioFonts = (): void => {
  if (typeof document === 'undefined' || studioFontsPromise || document.getElementById('studio-fonts')) {
    return;
  }
  studioFontsPromise = fetch(STUDIO_FONTS_HREF)
    .then(response => response.text())
    .then(css => {
      const renamed = FAMILY_RENAMES.reduce((acc, [from, to]) => acc.split(from).join(to), css);
      const style = document.createElement('style');
      style.id = 'studio-fonts';
      style.textContent = renamed;
      document.head.appendChild(style);
    })
    .catch(() => {
      studioFontsPromise = null;
    });
};
